import type { Page } from '../../types.js';

/**
 * The "which one do we pick" page.
 *
 * Two orthogonal choices get confused constantly and are therefore separated here:
 * the **shape** (who draws the screen) and the **platform** (what the customer is
 * holding). A bank picks one of each, and the second choice is mostly decided for
 * them by their existing app.
 */
const page: Page = {
  slug: 'surfaces',
  title: 'Surfaces',
  lede:
    'Two choices, not one. First, who draws the screen — us or you. Second, what the customer is holding — a browser, an iPhone, an Android phone or a Huawei device. The first is yours to make; the second is mostly made for you already.',
  group: 'Choosing a shape',
  blocks: [
    { kind: 'heading', text: 'Choice one: who draws the screen' },
    {
      kind: 'cards',
      columns: 4,
      items: [
        {
          title: 'Widget',
          text: 'Our screens in a sandboxed frame served from our origin. Fastest to a working product, and improvements reach your customers without you shipping anything.',
          accent: 'teal',
        },
        {
          title: 'Components',
          text: 'The same screens rendered inside your own React or React Native tree. Same DOM, same stylesheet, same focus order — and no sandbox.',
          accent: 'blue',
        },
        {
          title: 'SDK',
          text: 'Your screens over our logic. Prices, quotes, orders, holdings, session handling, retries and money arithmetic are all underneath.',
          accent: 'blue',
        },
        {
          title: 'API',
          text: 'Your servers call ours. No library of ours in a customer-facing app, and no screen at all — which is exactly right for back-office work.',
          accent: 'violet',
        },
      ],
    },
    {
      kind: 'table',
      columns: ['', 'Widget', 'Components', 'SDK', 'API'],
      rows: [
        ['Isolation from your page', 'Full — a sandboxed frame', 'None — it shares your DOM', 'Not applicable', 'Not applicable'],
        ['Design control', 'Tokens and wording', 'Tokens, wording and your cascade', 'Total', 'Total'],
        ['Engineering to a first trade', 'An afternoon', 'A day or two', 'A few days', 'A sprint'],
        ['To a production web launch', 'One to two weeks', 'Two to four weeks', 'Three to six weeks', 'Two to three months'],
        ['What your team builds', 'One backend route', 'That route, plus layout', 'That route, plus every screen', 'That route, every screen, and the logic between'],
        ['Where a pricing mistake lands', 'With us', 'With us', 'With us', 'With you'],
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      title: 'Start on the widget unless design governance forbids it',
      text: 'It puts a real product in front of real customers in days, which is the only way to test the commercial question. The backend route you build for it is the same one every other shape needs, so nothing is thrown away when you replace a screen later.',
    },
    {
      kind: 'flow',
      accent: 'teal',
      steps: [
        { title: 'Pilot on widgets', sub: 'Days' },
        { title: 'Measure', sub: 'Rate seen to trade confirmed' },
        { title: 'Replace one screen', sub: 'Components or SDK' },
        { title: 'Keep the rest', sub: 'Same session, same backend route' },
      ],
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Choice two: what the customer is holding' },
    {
      kind: 'table',
      columns: ['Platform', 'What ships', 'Where the signing key lives', 'What proves the app is genuine'],
      rows: [
        ['Web', 'A browser package and React hooks', 'Browser key store, marked non-extractable', 'The exact web address, registered with us'],
        ['Widget', 'A dependency-free loader, no React on your page', 'Same, inside the frame', 'A per-bank browser policy that decides where it may render'],
        ['React Native and Expo', 'One package bridging to the native modules', 'Secure Enclave on iOS, Keystore on Android', 'Whichever device check the phone supports'],
        ['Native iOS', 'A Swift package', 'Secure Enclave', 'App Attest, set up in your Apple account'],
        ['Native Android', 'An Android library, two flavours', 'Android Keystore, hardware-backed where available', 'Play Integrity, set up in your Google account'],
        ['Huawei without Play Services', 'The Huawei flavour of the same library', 'Huawei hardware key store', 'Safety Detect, set up in AppGallery Connect'],
      ],
    },
    {
      kind: 'prose',
      text: 'The customer-facing behaviour is identical on all six. What differs is where the private key is kept and what proves the app is your real build on a real device, and those two things are the entire per-platform difference.',
    },
    {
      kind: 'callout',
      tone: 'stop',
      title: 'No platform quietly lowers its own bar',
      text: 'If a device cannot supply a hardware-backed key, or has no device-check provider at all, the software refuses outright or starts a deliberately read-only session and tells us which provider ran. There is no silent fallback to a weaker key, because a fallback nobody can see is the one that reaches production.',
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Huawei is only your problem if it is your market',
      text: 'The Huawei path exists for markets where customers carry Huawei devices without Google services. If that is not your market, skip it entirely and carry none of its cost — it is a build flavour, not a second product.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Two asymmetries worth knowing before your architects plan' },
    {
      kind: 'cards',
      columns: 2,
      items: [
        {
          title: 'Mobile launches are gated by other people',
          text: 'A web launch goes when your own release process allows. A mobile launch waits on Apple, Google or Huawei, and on this kind of programme the store paperwork usually takes longer than the integration it is gating.',
          accent: 'rose',
        },
        {
          title: 'Device checks do not run in a simulator',
          text: 'The proof that an app is genuine needs a real, signed build on a real device. Your first end-to-end mobile test is on a physical handset, and your test plan needs to budget for that.',
          accent: 'rose',
        },
      ],
    },

    { kind: 'heading', text: 'Mixing shapes in one app' },
    {
      kind: 'table',
      columns: ['Shape', 'What a bank builds', 'What makes it hold together'],
      rows: [
        ['Widgets only', 'A rate panel on the home screen, a trade screen behind a button', 'One session per page load, managed for you'],
        ['SDK only', 'Fully custom screens in your own design system', 'One client for the whole app, created once'],
        ['SDK and widgets', 'Your portfolio screen, our trade screen', 'The SDK owns the session and the widget reuses it — never two sessions for one customer'],
        ['SDK and API', 'Live prices in the app, reconciliation overnight on your servers', 'Two registrations, two permission planes, one set of records underneath'],
        ['All of them', 'Our widgets, your screens, and your back-office jobs', 'A live stream plus a local channel keeps every surface consistent within a second'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'One customer, one session',
      text: 'Two surfaces in one app must never hold two sessions for the same person. Two sessions mean two device keys, two renewal timers and two chances to be ended independently, which is the kind of divergence that produces a bug nobody can reproduce.',
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Rollout',
          text: 'What onboarding involves, what we need from you, and roughly how long each stage takes.',
          href: '/business/rollout',
          accent: 'teal',
        },
        {
          title: 'Security and trust',
          text: 'What the device key and the app check are actually protecting, in review terms.',
          href: '/business/security-and-trust',
          accent: 'violet',
        },
        {
          title: 'For your engineers',
          text: 'The developer track. Sign-in required, because it carries request shapes and your own identifiers.',
          href: '/developer',
          accent: 'blue',
          cta: 'Open',
        },
      ],
    },
  ],
};

export default page;
